import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../data/users';
import { Icons } from '../utils/icons';
import * as keystoreService from '../services/keystoreService';

const CATEGORIAS = ['Proveedores', 'Sistemas internos', 'Email', 'Redes sociales', 'Financiero', 'Otros'];

const TODOS_LOS_ROLES = [ROLES.ADMIN, ROLES.ATENCION_CLIENTE, ROLES.COORDINADOR_OPERACIONES];

const ROLES_DISPLAY = {
  [ROLES.ADMIN]: 'Administrador',
  [ROLES.ATENCION_CLIENTE]: 'Atención al Cliente',
  [ROLES.COORDINADOR_OPERACIONES]: 'Coordinador de Operaciones',
};

const CATEGORIA_COLORS = {
  'Proveedores':        'bg-blue-100 text-blue-700',
  'Sistemas internos':  'bg-purple-100 text-purple-700',
  'Email':              'bg-green-100 text-green-700',
  'Redes sociales':     'bg-pink-100 text-pink-700',
  'Financiero':         'bg-yellow-100 text-yellow-700',
  'Otros':              'bg-gray-100 text-gray-600',
};

const FORM_VACIO = {
  title: '', category: 'Otros', username: '', password: '', url: '', notes: '',
  visiblePara: [...TODOS_LOS_ROLES],
};

export default function KeystoreView() {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;

  const [entries, setEntries]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [busqueda, setBusqueda]             = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [passwordsVisibles, setPasswordsVisibles] = useState({});
  const [copiado, setCopiado]               = useState(null);
  const [modal, setModal]                   = useState(null);
  const [confirmDelete, setConfirmDelete]   = useState(null);
  const [guardando, setGuardando]           = useState(false);
  const [form, setForm]                     = useState(FORM_VACIO);
  const [errorForm, setErrorForm]           = useState('');

  const cargarEntries = async () => {
    setLoading(true);
    try { setEntries(await keystoreService.fetchEntries()); }
    catch { /* deja la lista anterior si falla el refresco */ }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarEntries(); }, []);

  // El filtro por rol ya lo aplica manageKeystore.js en servidor.
  const entriesVisibles = entries;

  const entriesFiltradas = entriesVisibles.filter(e => {
    const q = busqueda.toLowerCase();
    const matchQ = !q ||
      e.title?.toLowerCase().includes(q) ||
      e.username?.toLowerCase().includes(q) ||
      e.url?.toLowerCase().includes(q) ||
      e.notes?.toLowerCase().includes(q);
    const matchCat = categoriaFiltro === 'todas' || e.category === categoriaFiltro;
    return matchQ && matchCat;
  });

  const copiar = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiado(key);
      setTimeout(() => setCopiado(null), 2000);
    } catch { /* clipboard permission denied */ }
  };

  const togglePassword = (id) =>
    setPasswordsVisibles(prev => ({ ...prev, [id]: !prev[id] }));

  const abrirCrear = () => {
    setForm(FORM_VACIO);
    setErrorForm('');
    setModal({ mode: 'crear' });
  };

  const abrirEditar = (entry) => {
    setForm({
      title:       entry.title       || '',
      category:    entry.category    || 'Otros',
      username:    entry.username    || '',
      password:    entry.password    || '',
      url:         entry.url         || '',
      notes:       entry.notes       || '',
      visiblePara: entry.visiblePara || [ROLES.ADMIN],
    });
    setErrorForm('');
    setModal({ mode: 'editar', entry });
  };

  const guardar = async () => {
    if (!form.title.trim()) { setErrorForm('El nombre es obligatorio.'); return; }
    if (form.visiblePara.length === 0) { setErrorForm('Selecciona al menos un rol.'); return; }
    setErrorForm('');
    setGuardando(true);
    try {
      if (modal.mode === 'crear') {
        await keystoreService.createEntry({ ...form, createdBy: user.name });
      } else {
        await keystoreService.updateEntry(modal.entry.id, form);
      }
      setModal(null);
      await cargarEntries();
    } catch (e) {
      setErrorForm(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    await keystoreService.deleteEntry(confirmDelete.id);
    setConfirmDelete(null);
    await cargarEntries();
  };

  const toggleRolVisible = (rol) =>
    setForm(prev => ({
      ...prev,
      visiblePara: prev.visiblePara.includes(rol)
        ? prev.visiblePara.filter(r => r !== rol)
        : [...prev.visiblePara, rol],
    }));

  return (
    <div className="max-w-5xl mx-auto">

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Icons.Lock className="w-7 h-7 text-red-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Keystore</h2>
              <p className="text-gray-500 text-sm mt-0.5">
                Contraseñas y credenciales de la empresa · {entriesVisibles.length} entrada{entriesVisibles.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={abrirCrear}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium text-sm"
            >
              <Icons.Plus className="w-4 h-4" />
              Nueva entrada
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow border p-4 mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 w-full">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, usuario, URL..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['todas', ...CATEGORIAS].map(cat => (
            <button
              key={cat}
              onClick={() => setCategoriaFiltro(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                categoriaFiltro === cat
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-red-400 hover:text-red-600'
              }`}
            >
              {cat === 'todas' ? 'Todas' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Cargando...</div>
      ) : entriesFiltradas.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          {entriesVisibles.length === 0
            ? 'No hay entradas todavía.'
            : 'No hay resultados para esta búsqueda.'}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {entriesFiltradas.map(entry => (
            <div key={entry.id} className="bg-white rounded-2xl shadow border border-gray-100 p-5 hover:shadow-md transition-shadow">

              {/* Cabecera tarjeta */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 truncate">{entry.title}</h3>
                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 font-medium ${CATEGORIA_COLORS[entry.category] || CATEGORIA_COLORS['Otros']}`}>
                    {entry.category}
                  </span>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <button
                      onClick={() => abrirEditar(entry)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Editar"
                    >
                      <Icons.Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(entry)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Eliminar"
                    >
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Campos */}
              <div className="space-y-2">
                {entry.username && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-400 mb-0.5">Usuario</p>
                      <p className="text-sm font-mono truncate text-gray-800">{entry.username}</p>
                    </div>
                    <button
                      onClick={() => copiar(entry.username, `${entry.id}-user`)}
                      className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-700 p-1"
                      title="Copiar usuario"
                    >
                      {copiado === `${entry.id}-user`
                        ? <span className="text-xs text-green-600 font-bold">✓</span>
                        : <Icons.Copy />}
                    </button>
                  </div>
                )}

                {entry.password && (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-400 mb-0.5">Contraseña</p>
                      <p className="text-sm font-mono truncate text-gray-800">
                        {passwordsVisibles[entry.id] ? entry.password : '••••••••••'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <button
                        onClick={() => togglePassword(entry.id)}
                        className="text-gray-400 hover:text-gray-700 p-1"
                        title={passwordsVisibles[entry.id] ? 'Ocultar' : 'Mostrar'}
                      >
                        {passwordsVisibles[entry.id]
                          ? <Icons.EyeOff className="w-4 h-4" />
                          : <Icons.Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => copiar(entry.password, `${entry.id}-pass`)}
                        className="text-gray-400 hover:text-gray-700 p-1"
                        title="Copiar contraseña"
                      >
                        {copiado === `${entry.id}-pass`
                          ? <span className="text-xs text-green-600 font-bold">✓</span>
                          : <Icons.Copy />}
                      </button>
                    </div>
                  </div>
                )}

                {entry.url && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400 mb-0.5">URL</p>
                    <a
                      href={entry.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline truncate block"
                    >
                      {entry.url}
                    </a>
                  </div>
                )}

                {entry.notes && (
                  <p className="text-xs text-gray-500 px-1 pt-1 italic">{entry.notes}</p>
                )}
              </div>

              {/* Roles visibles (solo admin) */}
              {isAdmin && entry.visiblePara?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-1">
                  {entry.visiblePara.map(r => (
                    <span key={r} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                      {ROLES_DISPLAY[r] || r}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal crear / editar */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">
                {modal.mode === 'crear' ? 'Nueva entrada' : 'Editar entrada'}
              </h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-700">
                <Icons.X />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre *</label>
                <input
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  placeholder="Ej: Google Workspace"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Categoría</label>
                <select
                  value={form.category}
                  onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                >
                  {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Usuario / Email</label>
                <input
                  value={form.username}
                  onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  placeholder="usuario@empresa.com"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Contraseña</label>
                <div className="relative">
                  <input
                    type={passwordsVisibles['modal'] ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:border-red-400"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => togglePassword('modal')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {passwordsVisibles['modal']
                      ? <Icons.EyeOff className="w-4 h-4" />
                      : <Icons.Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">URL (opcional)</label>
                <input
                  value={form.url}
                  onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notas (opcional)</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-none"
                  placeholder="Información adicional..."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Visible para</label>
                <div className="space-y-2">
                  {TODOS_LOS_ROLES.map(rol => (
                    <label key={rol} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.visiblePara.includes(rol)}
                        onChange={() => toggleRolVisible(rol)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">{ROLES_DISPLAY[rol]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {errorForm && (
                <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{errorForm}</p>
              )}
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setModal(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={guardar}
                disabled={guardando}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium"
              >
                {guardando ? 'Guardando...' : modal.mode === 'crear' ? 'Crear entrada' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar borrado */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Eliminar entrada</h3>
            <p className="text-gray-600 text-sm mb-6">
              ¿Seguro que quieres eliminar <strong>{confirmDelete.title}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={borrar}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
